import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import bg2 from '../assets/images/bg2.png';
import logoIzyko from '../assets/logos/izyko-black.png';
import { Loader2, ArrowLeft, Mail, KeyRound, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('email'); // 'email', 'code', 'password', 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authApi.requestPasswordReset(email);
      setStep('code');
      setResendCountdown(60);
      toast.success('Un code a été envoyé à votre email');
    } catch (error) {
      // Don't reveal if email exists
      setStep('code');
      setResendCountdown(60);
      toast.success('Si un compte existe avec cet email, un code a été envoyé');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }

    setLoading(true);

    try {
      await authApi.verifyResetCode(email, code);
      setStep('password');
    } catch (error) {
      const message = error.response?.data?.detail || 'Code invalide';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await authApi.completePasswordReset(email, code, newPassword);
      setStep('success');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la réinitialisation';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await authApi.requestPasswordReset(email);
      setResendCountdown(60);
      toast.success('Nouveau code envoyé');
    } catch (error) {
      toast.success('Si un compte existe avec cet email, un code a été envoyé');
      setResendCountdown(60);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile header */}
      <div className="lg:hidden p-4 flex items-center">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/login')}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
      </div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="hidden lg:inline-block mb-8">
            <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
          </Link>

          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <>
              <div className="text-center lg:text-left mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-6">
                  <KeyRound className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-3xl font-black mb-2">
                  Mot de passe oublié ?
                </h1>
                <p className="text-muted-foreground font-medium">
                  Entrez votre email pour recevoir un code de réinitialisation
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-bold">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@example.com"
                    required
                    data-testid="reset-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full py-6 text-base"
                  disabled={loading}
                  data-testid="reset-request-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Envoyer le code'
                  )}
                </Button>
              </form>

              <p className="mt-8 text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-foreground font-bold hover:underline">
                  Retour à la connexion
                </Link>
              </p>
            </>
          )}

          {/* Step 2: Enter Code */}
          {step === 'code' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-3xl font-black mb-2">
                  Vérifiez votre email
                </h1>
                <p className="text-muted-foreground font-medium">
                  Un code de vérification a été envoyé à
                </p>
                <p className="text-foreground font-bold mt-1">{email}</p>
              </div>

              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-bold">Code de vérification</Label>
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="text-center text-3xl font-mono tracking-[0.5em] h-16"
                    data-testid="reset-code-input"
                  />
                </div>

                <div className="flex items-start gap-2 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-sm text-yellow-800">
                    Si vous ne trouvez pas l'email, <strong>vérifiez vos spams</strong>.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full py-6 text-base"
                  disabled={loading || code.length !== 6}
                  data-testid="reset-verify-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Vérifier le code'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Vous n'avez pas reçu le code ?
                </p>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleResendCode}
                  disabled={loading || resendCountdown > 0}
                >
                  {resendCountdown > 0 ? `Renvoyer dans ${resendCountdown}s` : 'Renvoyer le code'}
                </Button>
              </div>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="text-foreground font-bold hover:underline"
                >
                  Modifier l'email
                </button>
              </p>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 'password' && (
            <>
              <div className="text-center lg:text-left mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-6">
                  <KeyRound className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-3xl font-black mb-2">
                  Nouveau mot de passe
                </h1>
                <p className="text-muted-foreground font-medium">
                  Choisissez un nouveau mot de passe sécurisé
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label className="font-bold">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pr-12"
                      data-testid="reset-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" strokeWidth={2} />
                      ) : (
                        <Eye className="h-5 w-5" strokeWidth={2} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-bold">Confirmer le mot de passe</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full py-6 text-base"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Changer le mot de passe'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" strokeWidth={1.5} />
              </div>
              <h1 className="font-heading text-3xl font-black mb-2">
                Mot de passe modifié !
              </h1>
              <p className="text-muted-foreground font-medium mb-8">
                Votre mot de passe a été réinitialisé avec succès.
              </p>
              <Button 
                className="rounded-full px-8"
                onClick={() => navigate('/login')}
              >
                Se connecter
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${bg2})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background/20 to-transparent" />
      </div>
    </div>
  );
}
